import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import {
  Award, CheckCircle2, XCircle, AlertTriangle, Minus, ChevronRight,
  ArrowRight, Play, Sparkles, BarChart3, Users, Building, Star,
  Database, Briefcase, MessageSquare, Linkedin, DollarSign, Rocket,
  Target, TrendingUp, Clock, Eye, Plus, X, Search
} from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import SituationStrip from '../components/SituationStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stagger = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const fadeUp = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

const STATUS_CFG = {
  strong:  { icon: CheckCircle2,  color: '#15BA89', label: 'Strong',  bg: 'rgba(21,186,137,0.1)',  border: 'rgba(21,186,137,0.3)' },
  partial: { icon: AlertTriangle, color: '#2E86E2', label: 'Partial', bg: 'rgba(46,134,226,0.1)',   border: 'rgba(46,134,226,0.3)' },
  gap:     { icon: XCircle,       color: '#39E3DC', label: 'Gap',     bg: 'rgba(57,227,220,0.08)',  border: 'rgba(57,227,220,0.25)' },
};

const CAT_ICONS = { database: Database, 'bar-chart': BarChart3, briefcase: Briefcase, linkedin: Linkedin, message: MessageSquare, users: Users };

const DEFAULT_MATRIX = {
  sql_data: 'partial', analytics_project: 'gap', internship: 'gap',
  linkedin_resume: 'partial', communication: 'partial', network_warmth: 'gap',
};

const FALLBACK_VALUES = {
  default: 'Values strong analytical and problem-solving skills aligned with their industry needs.',
};

export default function ProofBuilderPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeEmployers, setActiveEmployers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [proofStarted, setProofStarted] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/college/employer-proof-match`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setData(r.data); setActiveEmployers(r.data.employers.map(e => e.name)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  // Live company search via Clearbit Autocomplete (free, no API key)
  const searchCompanies = (query) => {
    setSearchQuery(query);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setSearchResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`https://autocomplete.clearbit.com/v1/companies/suggest?query=${encodeURIComponent(query)}`);
        const companies = await r.json();
        setSearchResults(companies.slice(0, 8));
      } catch {
        setSearchResults([]);
      }
      setSearching(false);
    }, 300);
  };

  const removeEmployer = (name) => {
    if (activeEmployers.length <= 1) { toast.error('Keep at least one target employer'); return; }
    setActiveEmployers(prev => prev.filter(n => n !== name));
    toast.success(`Removed ${name}`);
  };

  const addEmployer = (name, domain) => {
    if (activeEmployers.includes(name)) { toast.info(`${name} is already added`); return; }
    if (activeEmployers.length >= 5) { toast.error('Maximum 5 target employers'); return; }
    setActiveEmployers(prev => [...prev, name]);
    setShowAddModal(false);
    setSearchQuery('');
    setSearchResults([]);
    toast.success(`Added ${name} as target employer`);
  };

  const startProof = (title) => {
    setProofStarted(prev => ({ ...prev, [title]: true }));
    toast.success(`"${title}" marked as started! Track progress in your dashboard.`);
  };

  if (loading) return <AppLayout><div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div></AppLayout>;
  if (!data) return <AppLayout><div className="text-center py-20 text-[#C6D4E8]">Unable to load proof match data.</div></AppLayout>;

  const { student, match_score, employers: apiEmps, evidence_categories, matrix, next_proofs, employer_values, outcomes, fastest_path } = data;

  const visibleEmployers = activeEmployers.map(name => {
    const apiEmp = apiEmps.find(e => e.name === name);
    return apiEmp || { name, type: '', fit_score: 0 };
  });

  const fullMatrix = { ...matrix };
  activeEmployers.forEach(name => { if (!fullMatrix[name]) fullMatrix[name] = { ...DEFAULT_MATRIX }; });

  const fullValues = { ...employer_values };
  activeEmployers.forEach(name => { if (!fullValues[name]) fullValues[name] = FALLBACK_VALUES.default; });

  return (
    <AppLayout>
      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-4 max-w-[1400px] mx-auto" data-testid="proof-builder-page">

        {/* SITUATION STRIP (canonical context) */}
        <motion.div variants={fadeUp}><SituationStrip page="proof" /></motion.div>

        {/* HEADER */}
        <motion.div variants={fadeUp}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#F4F7FF]">
                Employer <span className="italic" style={{ background: 'linear-gradient(90deg, #782FE1, #39E3DC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Proof Match</span>
              </h1>
              <p className="text-sm text-[#C6D4E8]">See exactly what top employers need to believe before they hire you.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-[#C6D4E8]">Target Employers</span>
              {activeEmployers.map((name, i) => (
                <span key={name} className="text-xs text-white font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
                  style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(57,227,220,0.25)' }} data-testid={`employer-pill-${i}`}>
                  {name}
                  <button onClick={() => removeEmployer(name)} className="text-slate-300 hover:text-[#C01A49] transition-colors" data-testid={`remove-employer-${i}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <button onClick={() => { setShowAddModal(true); setTimeout(() => searchRef.current?.focus(), 100); }}
                className="text-xs text-[#39E3DC] hover:text-white transition-colors font-medium" data-testid="add-employer-btn">+ Add Employer</button>
            </div>
          </div>
        </motion.div>

        {/* ADD EMPLOYER, Live Search */}
        <AnimatePresence>
          {showAddModal && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              <div className="rounded-xl p-4" style={{ background: 'rgba(6,23,54,0.95)', border: '1px solid rgba(57,227,220,0.35)', boxShadow: '0 0 8px rgba(57,227,220,0.4), 0 0 20px rgba(57,227,220,0.15)' }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-white font-outfit font-bold text-sm">Search & Add Employer</p>
                  <button onClick={() => { setShowAddModal(false); setSearchQuery(''); setSearchResults([]); }} className="text-slate-300 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C6D4E8]" />
                  <input ref={searchRef} type="text" value={searchQuery} onChange={e => searchCompanies(e.target.value)}
                    placeholder="Search any company (e.g. Google, Deloitte, Nike)..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-500 outline-none"
                    style={{ background: 'rgba(11,31,70,0.8)', border: '1px solid rgba(57,227,220,0.22)' }}
                    data-testid="employer-search-input" />
                  {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#39E3DC] border-t-transparent rounded-full animate-spin" />}
                </div>
                {searchResults.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {searchResults.map((co) => (
                      <button key={co.domain} onClick={() => addEmployer(co.name, co.domain)}
                        className="rounded-lg p-3 text-left border border-[rgba(89,126,190,0.18)] bg-[#0B1F46] hover:border-[rgba(57,227,220,0.35)] hover:bg-[rgba(57,227,220,0.05)] transition-all flex items-center gap-2"
                        data-testid={`search-result-${co.domain}`}>
                        {co.logo && <img src={co.logo} alt="" className="w-6 h-6 rounded object-contain flex-shrink-0 bg-white/10" onError={e => e.target.style.display='none'} />}
                        <div className="min-w-0">
                          <p className="text-xs text-white font-semibold truncate">{co.name}</p>
                          <p className="text-[9px] text-[#C6D4E8] truncate">{co.domain}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                  <div className="text-center py-3">
                    <p className="text-xs text-[#C6D4E8]">No results for "{searchQuery}"</p>
                    <button onClick={() => addEmployer(searchQuery)} className="text-xs text-[#39E3DC] mt-1 hover:underline">
                      + Add "{searchQuery}" as custom employer
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* MATCH SCORE BAR, compact, shows who it's matched against */}
        <motion.div variants={fadeUp}>
          <div className="rounded-[14px] px-5 py-4 flex items-center gap-5" style={{
            background: 'rgba(6,23,54,0.72)', backdropFilter: 'blur(16px)',
            border: '1px solid rgba(57,227,220,0.28)',
            boxShadow: '0 0 8px rgba(57,227,220,0.45), 0 0 20px rgba(57,227,220,0.20)',
          }}>
            <div className="w-20 h-20 relative flex-shrink-0">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(89,126,190,0.15)" strokeWidth="2.5" />
                <motion.circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="2.5" strokeLinecap="round"
                  initial={{ strokeDasharray: '0 97.4' }} animate={{ strokeDasharray: `${match_score.score * 0.974} 97.4` }}
                  transition={{ duration: 1.2, ease: 'easeOut' }} stroke="url(#gaugeGrad)"
                  style={{ filter: 'drop-shadow(0 0 6px rgba(57,227,220,0.6))' }} />
                <defs><linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#782FE1" /><stop offset="100%" stopColor="#39E3DC" /></linearGradient></defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-outfit font-black text-white">{match_score.score}</span>
                <span className="text-[9px] text-[#C6D4E8]">/100</span>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-white font-outfit font-bold text-base">Employer Match Score</p>
                <span className="text-xs font-semibold text-[#15BA89]">{match_score.label}</span>
                <span className="text-[10px] text-[#39E3DC]">+{match_score.weekly_change} pts this week</span>
              </div>
              <p className="text-[11px] text-[#C6D4E8]">
                Matched against: <span className="text-white font-semibold">{activeEmployers.join(', ')}</span>
              </p>
              <p className="text-[10px] text-[#C6D4E8] mt-1">Close key gaps in 2-3 areas to reach a high-match candidate profile.</p>
            </div>
          </div>
        </motion.div>

        {/* EVIDENCE MATRIX, MAIN HERO SECTION with glow */}
        <motion.div variants={fadeUp}>
          <div className="rounded-[16px] p-5" style={{
            background: 'rgba(6,23,54,0.72)', backdropFilter: 'blur(16px)',
            border: '1px solid rgba(57,227,220,0.28)',
            boxShadow: '0 0 8px rgba(57,227,220,0.55), 0 0 20px rgba(57,227,220,0.25), 0 0 42px rgba(57,227,220,0.10)',
          }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-white font-outfit font-bold text-lg">What employers need to see from you</h2>
                <p className="text-xs text-[#C6D4E8]">Your evidence status across each target employer's requirements</p>
              </div>
              <div className="flex items-center gap-4 text-[10px]">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#15BA89]" /> Strong</span>
                <span className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-[#2E86E2]" /> Partial</span>
                <span className="flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5 text-[#39E3DC]" /> Gap</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgba(89,126,190,0.18)]">
                    <th className="text-left py-3 text-[#C6D4E8] font-semibold text-xs">Evidence Category</th>
                    {visibleEmployers.map((emp, i) => (
                      <th key={i} className="text-center py-3 text-white font-bold text-xs">{emp.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {evidence_categories.map((cat) => {
                    const Icon = CAT_ICONS[cat.icon] || Award;
                    return (
                      <tr key={cat.id} className="border-b border-[rgba(89,126,190,0.08)] hover:bg-[rgba(57,227,220,0.02)] transition-colors">
                        <td className="py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(57,227,220,0.06)', border: '1px solid rgba(57,227,220,0.15)' }}>
                              <Icon className="w-3.5 h-3.5 text-[#39E3DC]" />
                            </div>
                            <span className="text-white font-medium text-[13px]">{cat.label}</span>
                          </div>
                        </td>
                        {visibleEmployers.map((emp, j) => {
                          const status = fullMatrix[emp.name]?.[cat.id] || 'gap';
                          const cfg = STATUS_CFG[status];
                          const SIcon = cfg.icon;
                          return (
                            <td key={j} className="text-center py-3">
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                                style={{ color: cfg.color, backgroundColor: cfg.bg, border: `1px solid ${cfg.border}` }}>
                                <SIcon className="w-3 h-3" /> {cfg.label}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        {/* SIDEBAR ROW: Fastest Path + Strategy Sim */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <motion.div variants={fadeUp}>
            <div className="rounded-[14px] p-5 h-full" style={{
              background: 'linear-gradient(145deg, rgba(19,22,58,0.92), rgba(27,13,64,0.82))',
              border: '1px solid rgba(120,47,225,0.55)',
              boxShadow: '0 0 8px rgba(120,47,225,0.45), 0 0 20px rgba(120,47,225,0.20)',
            }}>
              <div className="flex items-center gap-2 mb-3">
                <Rocket className="w-5 h-5 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 4px rgba(57,227,220,0.5))' }} />
                <p className="text-white font-outfit font-bold text-base">Fastest path to improve your score</p>
              </div>
              <p className="text-xs text-[#C6D4E8] mb-4">Close 2 key gaps to reach 80+ match score.</p>
              <div className="space-y-2.5 mb-5">
                {fastest_path.map((item, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg p-2.5" style={{ background: 'rgba(57,227,220,0.04)', border: '1px solid rgba(57,227,220,0.12)' }}>
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white" style={{ background: 'rgba(120,47,225,0.2)', border: '1px solid rgba(120,47,225,0.35)' }}>{i + 1}</span>
                      <span className="text-xs text-white font-medium">{item.title}</span>
                    </div>
                    <span className="text-xs font-bold text-[#39E3DC]" style={{ textShadow: '0 0 6px rgba(57,227,220,0.3)' }}>+{item.points} pts</span>
                  </div>
                ))}
              </div>
              <Link to="/app/career-intelligence" className="fp-btn-primary block text-center py-3 rounded-xl text-sm" data-testid="view-roadmap-btn">
                View Personalized Roadmap
              </Link>
            </div>
          </motion.div>

          <motion.div variants={fadeUp}>
            <Link to="/app/path-simulator" className="block rounded-[14px] p-5 h-full hover:scale-[1.005] transition-transform" style={{
              background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(57,227,220,0.22)',
              boxShadow: '0 0 6px rgba(57,227,220,0.2)',
            }} data-testid="open-strategy-sim">
              <div className="flex items-center gap-2 mb-3">
                <Play className="w-5 h-5 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 4px rgba(57,227,220,0.5))' }} />
                <p className="text-white font-outfit font-bold text-base">Strategy Simulator</p>
              </div>
              <p className="text-xs text-[#C6D4E8] mb-4">See how building specific proofs changes your salary, job landing rate, and career trajectory over 10 years.</p>
              <span className="fp-btn-primary block text-center py-3 rounded-xl text-sm">Open Strategy Simulator</span>
            </Link>
          </motion.div>
        </div>

        {/* BUILD THESE PROOFS NEXT */}
        <motion.div variants={fadeUp}>
          <div className="rounded-[14px] p-5" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(89,126,190,0.22)' }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-white font-outfit font-bold text-base">Build These Proofs Next</h2>
                <p className="text-[10px] text-[#C6D4E8]">High-impact steps to close your biggest gaps.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {next_proofs.map((proof, i) => {
                const started = proofStarted[proof.title];
                return (
                  <div key={i} className="rounded-xl p-4" style={{
                    background: 'rgba(11,31,70,0.6)',
                    border: `1px solid ${started ? 'rgba(21,186,137,0.3)' : 'rgba(57,227,220,0.18)'}`,
                    boxShadow: started ? '0 0 8px rgba(21,186,137,0.2)' : 'inset 0 0 12px rgba(57,227,220,0.03)',
                  }}>
                    <p className="text-white font-outfit font-bold text-sm">{proof.title}</p>
                    <p className="text-[10px] text-[#C6D4E8] mt-1 leading-relaxed">{proof.desc}</p>
                    <div className="flex items-center gap-4 mt-2 text-[10px]">
                      <div><span className="text-[#C6D4E8]">Impact</span><br/><span className="font-bold text-[#39E3DC]">+{proof.impact} pts</span></div>
                      <div><span className="text-[#C6D4E8]">Time</span><br/><span className="font-bold text-white">{proof.time}</span></div>
                    </div>
                    {started ? (
                      <div className="mt-3 flex items-center gap-2 py-2 px-3 rounded-lg text-xs font-semibold text-[#15BA89]"
                        style={{ background: 'rgba(21,186,137,0.08)', border: '1px solid rgba(21,186,137,0.2)' }}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> In Progress
                      </div>
                    ) : (
                      <button onClick={() => startProof(proof.title)}
                        className="mt-3 flex items-center justify-center gap-2 w-full py-2 rounded-lg text-xs font-semibold text-white transition-all hover:scale-[1.02]"
                        style={{ background: 'rgba(57,227,220,0.08)', border: '1px solid rgba(57,227,220,0.25)' }}
                        data-testid={`start-proof-${i}`}>
                        {proof.cta} <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* EMPLOYER VALUES + OUTCOMES */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <motion.div variants={fadeUp}>
            <h2 className="text-white font-outfit font-bold text-base mb-2">What each employer values</h2>
            <div className="space-y-2">
              {visibleEmployers.map((emp, i) => (
                <div key={i} className="rounded-xl p-3 flex items-start gap-3" style={{ background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(89,126,190,0.18)' }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                    style={{ background: 'linear-gradient(135deg, rgba(120,47,225,0.2), rgba(46,134,226,0.2))', border: '1px solid rgba(120,47,225,0.3)' }}>
                    {emp.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-xs text-white font-semibold">{emp.name}</p>
                    <p className="text-[10px] text-[#C6D4E8] leading-relaxed mt-0.5">{fullValues[emp.name]}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeUp}>
            <h2 className="text-white font-outfit font-bold text-base mb-2">How proofs change your outcomes</h2>
            <div className="grid grid-cols-3 gap-2">
              {outcomes.map((o, i) => (
                <div key={i} className="rounded-xl p-3 text-center" style={{
                  background: 'rgba(6,23,54,0.72)', border: '1px solid rgba(57,227,220,0.22)',
                  boxShadow: '0 0 6px rgba(57,227,220,0.15), inset 0 0 10px rgba(57,227,220,0.03)',
                }}>
                  <p className="text-[10px] text-[#C6D4E8] mb-1">{o.label}</p>
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="text-lg font-outfit font-black text-[#C6D4E8]">{o.before}</span>
                    <ArrowRight className="w-3 h-3 text-[#39E3DC]" />
                    <span className="text-lg font-outfit font-black text-white" style={{ textShadow: '0 0 10px rgba(57,227,220,0.25)' }}>{o.after}</span>
                  </div>
                  <span className="text-[10px] font-bold text-[#39E3DC]">{o.delta}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AppLayout>
  );
}
